import { useEffect, useState } from "react";
import { BellRing, Send, Save, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString("nl-NL", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");
const reasonLabel = (r) => ({ lag: "event-loop", errors: "foutpercentage" }[r] || r);
const field = "border-2 border-foreground bg-background px-2 py-1 font-mono-num text-sm w-24 text-right";
const FIELDS = ["enabled", "lag_ms", "error_pct", "min_requests", "cooldown_min", "recovery_min"];
const badge = "px-1.5 py-px border font-display uppercase text-[9px] tracking-widest";

const HistoryRow = ({ h, i }) => {
  const recovery = h.kind === "recovery";
  const tone = h.test ? "border-muted-foreground text-muted-foreground" : recovery ? "border-positive text-positive" : "border-destructive text-destructive";
  const label = recovery ? (h.test ? "herstel (test)" : "herstel") : h.test ? "test" : (h.reasons || []).map(reasonLabel).join(" + ") || "alarm";
  return (
    <li className="px-3 py-1.5 flex items-center gap-2 flex-wrap font-mono-num" data-testid={`load-alert-history-row-${i}`}>
      <span>{h.at}</span>
      <span className={`${badge} ${tone}`} data-testid={`load-alert-history-kind-${i}`}>{label}</span>
      {recovery && !h.test && <span>na {h.duration_min} min · {h.alerts} alarm(en)</span>}
      <span>loop {h.lag_ms} ms · fouten {h.error_pct}% ({h.errors}/{h.requests})</span>
      <span className={h.sent ? "text-positive" : "text-destructive"}>{h.sent ? `verzonden → ${h.to}` : `niet verzonden: ${h.error || "?"}`}</span>
    </li>
  );
};

const IncidentStatus = ({ s }) => {
  if (s.incident) {
    return <span className={`${badge} border-destructive text-destructive`} data-testid="load-alert-incident">Storing actief sinds {fmtWhen(s.incident.started_at)} · {s.incident.alerts} alarm(en)</span>;
  }
  const last = s.history.find((h) => !h.test);
  if (last?.kind === "recovery") {
    return <span className={`${badge} border-positive text-positive inline-flex items-center gap-1`} data-testid="load-alert-recovered"><CheckCircle2 className="w-3 h-3" /> Hersteld {last.at}</span>;
  }
  return null;
};

// Belastingsalarm: grenzen, afkoelperiode en hersteltijd instellen, testmail sturen, laatste alarmen en herstel bekijken.
export default function LoadAlertCard() {
  const [s, setS] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/admin/load-alerts"); setS(data); setDraft(Object.fromEntries(FIELDS.map((k) => [k, data[k]]))); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy("save");
    try { const { data } = await api.put("/admin/load-alerts", Object.fromEntries(FIELDS.map((k) => [k, k === "enabled" ? draft[k] : Number(draft[k])]))); setS(data); toast.success("Alarminstellingen opgeslagen."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy("");
  };
  const test = (kind) => async () => {
    setBusy(kind);
    try { const { data } = await api.post(`/admin/load-alerts/test?kind=${kind}`); data.sent ? toast.success(`${kind === "recovery" ? "Testherstelmelding" : "Testalarm"} verstuurd naar ${data.to}.`) : toast.error(data.error || "Versturen mislukt."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy("");
  };

  if (!s || !draft) return null;
  const set = (k) => (e) => setDraft({ ...draft, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const btn = "btn-brutal px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5 disabled:opacity-50";

  return (
    <div className="border-2 border-foreground bg-card" data-testid="load-alert-card">
      <div className="px-3 py-2 border-b-2 border-foreground flex items-center justify-between gap-2 flex-wrap">
        <p className="font-display uppercase font-bold text-xs tracking-widest inline-flex items-center gap-1.5"><BellRing className="w-4 h-4" /> Belastingsalarm per e-mail</p>
        <label className="inline-flex items-center gap-2 text-xs font-display uppercase tracking-widest">
          <input type="checkbox" checked={draft.enabled} onChange={set("enabled")} data-testid="load-alert-enabled" className="w-4 h-4 accent-primary" /> {draft.enabled ? "Aan" : "Uit"}
        </label>
      </div>
      <div className="p-3 space-y-3">
        <p className="text-[11px] text-muted-foreground">Elke minuut toetst de monitor de laatste meting. Overschrijdt de event-loop-vertraging óf het foutpercentage (HTTP 5xx over de laatste 5 minuten) zijn grens, dan gaat er één mail naar het beheeradres; daarna is het alarm een afkoelperiode stil. Blijven de waarden na een alarm de ingestelde hersteltijd aaneengesloten onder de grens, dan volgt één korte "alles weer normaal"-mail.</p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
          <label className="space-y-1"><span className="stat-label">Event-loop ≥ (ms)</span><input type="number" min={100} max={60000} value={draft.lag_ms} onChange={set("lag_ms")} data-testid="load-alert-lag" className={field} /></label>
          <label className="space-y-1"><span className="stat-label">Fouten ≥ (%)</span><input type="number" min={0.1} max={100} step={0.1} value={draft.error_pct} onChange={set("error_pct")} data-testid="load-alert-error-pct" className={field} /></label>
          <label className="space-y-1"><span className="stat-label">Min. verzoeken (5 min)</span><input type="number" min={1} max={10000} value={draft.min_requests} onChange={set("min_requests")} data-testid="load-alert-min-requests" className={field} /></label>
          <label className="space-y-1"><span className="stat-label">Afkoelperiode (min)</span><input type="number" min={5} max={1440} value={draft.cooldown_min} onChange={set("cooldown_min")} data-testid="load-alert-cooldown" className={field} /></label>
          <label className="space-y-1"><span className="stat-label">Herstelmail na (min)</span><input type="number" min={1} max={1440} value={draft.recovery_min} onChange={set("recovery_min")} data-testid="load-alert-recovery" className={field} /></label>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button" onClick={save} disabled={busy !== ""} data-testid="load-alert-save" className={`${btn} bg-primary text-primary-foreground`}>
            {busy === "save" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Opslaan
          </button>
          <button type="button" onClick={test("alert")} disabled={busy !== ""} data-testid="load-alert-test" className={`${btn} bg-background text-foreground`}>
            {busy === "alert" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Stuur testmail
          </button>
          <button type="button" onClick={test("recovery")} disabled={busy !== ""} data-testid="load-alert-test-recovery" className={`${btn} bg-background text-foreground`}>
            {busy === "recovery" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Test herstelmail
          </button>
          <span className="text-[11px] text-muted-foreground font-mono-num" data-testid="load-alert-last-sent">Laatste alarm: {fmtWhen(s.last_sent_at)}</span>
          <IncidentStatus s={s} />
        </div>
        <div className="border-2 border-border" data-testid="load-alert-history">
          <p className="px-3 py-1.5 border-b border-border stat-label">Laatste alarmen en herstel</p>
          {s.history.length === 0 ? <p className="px-3 py-2 text-xs text-muted-foreground" data-testid="load-alert-history-empty">Nog geen alarm verstuurd.</p> : (
            <ul className="divide-y divide-border text-xs">{s.history.slice(0, 8).map((h, i) => <HistoryRow key={`${h.at}-${i}`} h={h} i={i} />)}</ul>
          )}
        </div>
      </div>
    </div>
  );
}
